import React from 'react';
import { useTranslation } from 'react-i18next';
import { alpha } from '@mui/material/styles';
import { Box, Typography, TextField, Slider } from '@mui/material';

import { opsColors, opsElevation } from '../../../../theme/opsTokens';

interface RideTypesSliderProps {
  rideTypes: any[];
  setRideTypes: React.Dispatch<React.SetStateAction<any[]>>;
}

const RideTypesSlider: React.FC<RideTypesSliderProps> = ({ rideTypes, setRideTypes }) => {
  const { t } = useTranslation('mapWizards');
  const departureValue = Math.round((rideTypes.find((rt: any) => rt.type === 'departure_fixed')?.probability || 0) * 100);
  const arrivalValue = 100 - departureValue;
  const preventWheelValueChange = (event: React.WheelEvent<HTMLInputElement>) => {
    event.currentTarget.blur();
  };
  const handleChange = (_: any, value: number | number[]) => {
    const v = typeof value === 'number' ? value : value[0];
    setRideTypes([
      { type: 'departure_fixed', probability: v / 100 },
      { type: 'arrival_fixed', probability: 1 - v / 100 },
    ]);
  };
  const handleInputChange = (side: 'departure' | 'arrival') => (e: React.ChangeEvent<HTMLInputElement>) => {
    let v = parseInt(e.target.value, 10);
    if (isNaN(v)) v = 0;
    v = Math.max(0, Math.min(100, v));
    if (side === 'departure') {
      setRideTypes([
        { type: 'departure_fixed', probability: v / 100 },
        { type: 'arrival_fixed', probability: 1 - v / 100 },
      ]);
    } else {
      setRideTypes([
        { type: 'departure_fixed', probability: 1 - v / 100 },
        { type: 'arrival_fixed', probability: v / 100 },
      ]);
    }
  };
  return (
    <Box sx={{ mt: 0.75, mb: 1.5 }}>
      <Typography variant="h6" sx={{ textAlign: 'center', mb: 0.75 }}>
        {t('generateRides.rideTypes.title')}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5, textAlign: 'center', color: 'text.secondary' }}>
        {t('generateRides.rideTypes.description')}
      </Typography>
      <Box
        sx={(theme) => ({
          px: { xs: 1.5, sm: 2.25 },
          py: 1.5,
          borderRadius: 2.5,
          border: `1px solid ${opsColors.neutral.line}`,
          backgroundColor: alpha(opsColors.neutral.panel, 0.62),
          boxShadow: opsElevation.raised.shadow,
        })}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
          <Box sx={{ textAlign: 'center', flex: 1, minWidth: 170 }}>
            <Typography variant="h5" sx={{ mb: 0.75 }}>
              {t('generateRides.rideTypes.departureFixed')}
            </Typography>
            <TextField
              type="number"
              value={departureValue}
              onChange={handleInputChange('departure')}
              onWheel={preventWheelValueChange}
              inputProps={{ min: 0, max: 100, style: { textAlign: 'center' } }}
              size="small"
              sx={{ width: 88 }}
            />
          </Box>
          <Box sx={{ flex: 1.5, minWidth: 190, px: { xs: 0.25, sm: 1.25 } }}>
            <Slider
              value={departureValue}
              onChange={handleChange}
              min={0}
              max={100}
              step={1}
              valueLabelDisplay="off"
              sx={(theme) => ({
                '& .MuiSlider-rail': {
                  height: 6,
                  opacity: 0.35,
                },
                '& .MuiSlider-track': {
                  backgroundColor: opsColors.brand.primary,
                  height: 6,
                  border: 'none',
                },
                '& .MuiSlider-thumb': {
                  width: 24,
                  height: 24,
                },
              })}
            />
          </Box>
          <Box sx={{ textAlign: 'center', flex: 1, minWidth: 170 }}>
            <Typography variant="h5" sx={{ mb: 0.75 }}>
              {t('generateRides.rideTypes.arrivalFixed')}
            </Typography>
            <TextField
              type="number"
              value={arrivalValue}
              onChange={handleInputChange('arrival')}
              onWheel={preventWheelValueChange}
              inputProps={{ min: 0, max: 100, style: { textAlign: 'center' } }}
              size="small"
              sx={{ width: 88 }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default RideTypesSlider; 
